import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import RequirePermission from './components/RequirePermission';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerForm from './pages/CustomerForm';
import Loans from './pages/Loans';
import LoanForm from './pages/LoanForm';
import LoanDetail from './pages/LoanDetail';
import Accounts from './pages/Accounts';
import AccountStatement from './pages/AccountStatement';
import Expenses from './pages/Expenses';
import Income from './pages/Income';
import Collections from './pages/Collections';
import UserBalance from './pages/UserBalance';
import Users from './pages/Users';
import PLSheet from './pages/PLSheet';
import ClientScore from './pages/ClientScore';
import Login from './pages/Login';
import { AuthProvider, useAuth } from './context/AuthContext';

// Collectors can never see Dashboard, so the default landing route can't
// just be a hardcoded /dashboard redirect anymore — it needs to know who's
// logged in first.
function HomeRedirect() {
  const { user } = useAuth();
  const target = (user && user.role === 'collector') ? '/collections' : '/dashboard';
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeRedirect />} />
            {/* Every page below is gated by the user's actual saved
                permission, not just their role — see RequirePermission.js.
                Dashboard is hard-blocked for collectors inside
                hasPermission() regardless of any toggle. */}
            <Route path="dashboard" element={<RequirePermission perm="dashboard"><Dashboard /></RequirePermission>} />
            <Route path="customers" element={<RequirePermission perm="customers"><Customers /></RequirePermission>} />
            <Route path="customers/new" element={<RequirePermission perm="customers"><CustomerForm /></RequirePermission>} />
            <Route path="customers/:id/edit" element={<RequirePermission perm="customers"><CustomerForm /></RequirePermission>} />
            <Route path="loans" element={<RequirePermission perm="loans"><Loans /></RequirePermission>} />
            <Route path="loans/new" element={<RequirePermission perm="loans"><LoanForm /></RequirePermission>} />
            <Route path="loans/:id" element={<RequirePermission perm="loans"><LoanDetail /></RequirePermission>} />
            <Route path="accounts" element={<RequirePermission perm="accounts"><Accounts /></RequirePermission>} />
            <Route path="accounts/:id/statement" element={<RequirePermission perm="accounts"><AccountStatement /></RequirePermission>} />
            <Route path="expenses" element={<RequirePermission perm="expenses"><Expenses /></RequirePermission>} />
            <Route path="income" element={<RequirePermission perm="income"><Income /></RequirePermission>} />
            <Route path="collections" element={<RequirePermission perm="collections"><Collections /></RequirePermission>} />
            {/* P&L has no dedicated permission toggle in the schema — kept
                admin-only, matching its previous role-based restriction. */}
            <Route path="pl-sheet" element={<RequirePermission adminOnly><PLSheet /></RequirePermission>} />
            <Route path="client-score" element={<RequirePermission adminOnly><ClientScore /></RequirePermission>} />
            <Route path="users" element={<RequirePermission adminOnly><Users /></RequirePermission>} />
            <Route path="my-balance" element={<RequirePermission perm="my_balance"><UserBalance /></RequirePermission>} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
